import { FileAddOutlined, UploadOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, Col, Form, Input, message, Row, Upload, Radio } from 'antd';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HOME_PRODUCT } from '../../../../constants/define/translation';
import { appStyles, defaultImage, formScrolling, selectSelector } from '../../../../styles';
import { CONFIG_CONTAINER_NAME, uploadFileToBlob, UploadFileToBlobInterface } from '../../../../utils/azure';
import { ButtonActionPage, SelectOption, SpinLoading } from '../../../atoms';
import { DragDropImage } from '../../../molecules/upload';
import { CountryContext } from '../../../../repository/context/countryContext';
import { PreviewHomeProduct } from '../../../molecules/preview/common/PreviewHomeProduct';
import { HomeProductStatus, HomeProductType } from '../../../../types/homeProduct';
import {
  patchUpdateHomeProductRepository,
  postCreateHomeProductRepository,
} from '../../../../repository/mytm/home-product/homeProductRepository';
import { getFuLLUrlEcom, URLType, URLTypeList } from '../../../../constants/common';

interface FormHomeProductProps {
  record?: HomeProductType;
  onRefresh: () => void;
  onCloseModal: () => void;
  setDataRecord: Function;
  countrySelected: string;
}

const azureCDN = process.env.REACT_APP_AZURE_CDN;
const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;

export const FormHomeProduct = (props: FormHomeProductProps) => {
  const { t } = useTranslation(HOME_PRODUCT);
  const { record, onRefresh, onCloseModal, setDataRecord, countrySelected } = props;
  const [form] = Form.useForm();
  const options = record?.options ? JSON.parse(record?.options) : null;
  const defaultValue = {
    productName: record?.productName,
    brandLogo: record?.brandLogo,
    link: record?.link || '',
    country: record ? record?.country : countrySelected || 'USA',
    tag: record?.tag,
    ctaLinkType: options?.ctaLinkType || URLType.PDP,
  };
  const [isCreateLoading, setCreateLoading] = useState(false);
  const [isBlockCtaLink, setBlockCtaLink] = useState(false);
  const [imageList, setImageList] = useState<UploadFileToBlobInterface[]>([]);
  const [messageErrorUploadImage, setMessageErrorUploadImage] = useState<string>();
  const [selectedImage, setSelectedImage] = useState<number>(0);
  const [dataForm, setDataForm] = useState<any | undefined>(defaultValue);
  const countryContext = useContext(CountryContext);

  const countryOptions: any[] = countryContext?.countryList?.map(item => {
    return {
      value: item?.code,
      label: item?.name,
    };
  });

  const onSetImageList = useCallback((data: UploadFileToBlobInterface[]) => {
    setImageList([...data?.filter(item => !item.isLogo), ...data?.filter(item => item.isLogo)]);
  }, []);

  const onSelectedImage = useCallback((index: number) => {
    setSelectedImage(index);
  }, []);

  const onChangeInput = useCallback(
    e => {
      const newField = form.getFieldsValue();
      let ctaNewLink = newField?.link || '';
      if (
        newField &&
        newField.ctaLinkType &&
        ![URLType.PDP, URLType.CUSTOM, URLType.GRID_PAGE].includes(newField.ctaLinkType)
      ) {
        const dataUrl = getFuLLUrlEcom(newField?.ctaLinkType, newField?.country);
        ctaNewLink = dataUrl?.ctaNewLink;
        setBlockCtaLink(dataUrl?.blockLink);

        form.setFieldsValue({
          ...newField,
          link: ctaNewLink,
        });
      } else {
        setBlockCtaLink(false);
      }
      setDataForm((old: any) => ({
        ...old,
        productName: newField?.productName,
        link: ctaNewLink,
        logo: newField?.logo,
        tag: newField?.tag,
        country: newField?.country,
        ctaLinkType: newField?.ctaLinkType,
      }));
    },
    [form],
  );

  const onFinish = useCallback(
    (formValue: any) => {
      const imgListNotUpload = imageList.filter(img => !img.isImageURL);
      const logoIndex = imgListNotUpload.findIndex(img => img.isLogo);
      const backgroundIndex = imgListNotUpload.findIndex(img => !img.isLogo);
      const imgLink = imageList.filter(img => img.isImageURL);
      const imgBackground = imageList.filter(img => !img.isLogo)?.[0]?.dataURL;
      if (!imgBackground) {
        setMessageErrorUploadImage(t('validate.imageBlank'));
        return;
      }
      setCreateLoading(true);
      uploadFileToBlob(imgListNotUpload, CONFIG_CONTAINER_NAME.UPLOADS)
        .then((images: any[]) => {
          let newImagesCDN = images;
          if (azureCDN) {
            newImagesCDN = images.map(url => url.replace(`${storageAccountName}.blob.core.windows.net`, azureCDN));
          }
          const imageUrl =
            newImagesCDN && newImagesCDN.length && backgroundIndex >= 0
              ? newImagesCDN[backgroundIndex]
              : imgLink.filter(img => !img.isLogo).map(v => v.dataURL)[0];
          const logoURL =
            newImagesCDN && newImagesCDN.length && logoIndex >= 0
              ? newImagesCDN?.[logoIndex]
              : imgLink.filter(img => img.isLogo).map(v => v.dataURL)[0];
          const countryStr = form.getFieldValue('country').toString();
          const newOptions = {
            ctaLinkType: dataForm?.ctaLinkType,
          };
          const payloadToPost = {
            productName: dataForm?.productName,
            image: imageUrl || '',
            brandLogo: logoURL || '',
            link: dataForm?.link,
            country: countryStr,
            tag: dataForm?.tag,
            status: HomeProductStatus.INACTIVE,
            options: newOptions,
          };
          if (record) {
            return patchUpdateHomeProductRepository({
              ...payloadToPost,
              status: record?.status,
              id: record?.id,
            })
              .then(() => {
                onRefresh();
                onCloseModal();
                message.success(t('message.success.updateHomeProduct'));
              })
              .catch(e => {
                setCreateLoading(true);
                message.error(e?.errorMessage);
              })
              .finally(() => {
                setCreateLoading(false);
              });
          }
          return postCreateHomeProductRepository(payloadToPost)
            .then(() => {
              onRefresh();
              onCloseModal();
              message.success(t('message.success.createHomeProduct'));
            })
            .catch(e => {
              setCreateLoading(true);
              message.error(e?.errorMessage);
            })
            .finally(() => {
              setCreateLoading(false);
              setDataRecord(null);
            });
        })
        .catch(() => {
          setCreateLoading(true);
          message.error(t('message.error.uploadImage'));
        })
        .finally(() => {
          setCreateLoading(false);
        });
    },
    [record, imageList, dataForm],
  );

  useEffect(() => {
    if (record) {
      const imgList: UploadFileToBlobInterface[] = [{ dataURL: record?.image, isImageURL: true }];
      if (record?.brandLogo) {
        imgList.push({ dataURL: record?.brandLogo, isLogo: true, isImageURL: true });
      }
      setImageList(imgList);
      setSelectedImage(0);
      setDataForm({
        ...defaultValue,
        country: record?.country,
      });
      if (options && options.ctaLinkType) {
        if (
          options.ctaLinkType === URLType.PDP ||
          options.ctaLinkType === URLType.CUSTOM ||
          options.ctaLinkType === URLType.GRID_PAGE
        ) {
          setBlockCtaLink(false);
        } else {
          setBlockCtaLink(true);
        }
      }
    }
  }, [record]);

  useEffect(() => {
    if (imageList.length) {
      setMessageErrorUploadImage('');
    }
  }, [imageList]);

  const action = {
    beforeUpload(file: any): Promise<any> {
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const img = document.createElement('img');
          img.src = reader?.result ? reader?.result.toString() : '';
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(img, 0, 0);
            }
            const url = canvas.toDataURL();
            canvas.toBlob(
              blob => {
                const uploadedFiles = [
                  {
                    dataURL: url,
                    blob,
                    fileName: file.name,
                    fileType: file.type,
                    isImageURL: false,
                    isLogo: true,
                  },
                ];
                const listImg = [...imageList.filter(item => !item.isLogo), ...uploadedFiles];
                onSetImageList(listImg);
                onSelectedImage(listImg.length - 1);
                resolve(blob);
              },
              file.type || 'image/jpeg',
              1,
            );
          };
        };
      });
    },
  };

  return (
    <>
      <SpinLoading isLoading={isCreateLoading} absolute>
        <SwitchContainer>
          <Form form={form} layout="vertical" initialValues={defaultValue} onFinish={onFinish} name="form-HomeProduct">
            <Row className="form-HomeProduct-container" gutter={[24, 24]}>
              <StyledSelectImageCol span={9}>
                <DragDropImage
                  {...{
                    imageList,
                    onSetImageList,
                    messageErrorUploadImage,
                    selectedImage,
                    onSelectedImage,
                    maxImage: 1,
                    nodePreview: (
                      <PreviewHomeProduct
                        values={{
                          ...dataForm,
                          backgroundImage: imageList?.length && imageList?.find(item => !item.isLogo)?.dataURL,
                          brandLogo: imageList?.length && imageList?.find(item => item.isLogo)?.dataURL,
                          describeText: t('form.imageUpload'),
                          describeText2: t('form.image1x1'),
                        }}
                        isPreview
                        className="preview-home-product"
                        height="186px"
                      />
                    ),
                  }}
                />
              </StyledSelectImageCol>
              <StyledFormHomeProduct span={15}>
                <div className="form-scroll">
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="country"
                        label={t('form.country')}
                        rules={[{ required: true, message: t('validate.countryBlank') }]}
                      >
                        <Radio.Group options={countryOptions} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="productName"
                        label={t('form.title')}
                        rules={[{ required: true, message: t('validate.titleBlank') }]}
                      >
                        <Input placeholder={t('form.title')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="tag"
                        label={t('form.tag')}
                        rules={[{ required: true, message: t('validate.tagBlank') }]}
                      >
                        <Input placeholder={t('form.tag')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <SelectOption
                        modeForm
                        name="ctaLinkType"
                        label={t('form.linkType')}
                        rules={[{ required: true, message: t('validate.linkTypeBlank') }]}
                        values={URLTypeList}
                        textDisplay="text"
                        onChange={onChangeInput}
                        showSearch
                        allowClear
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name="link"
                        label={t('form.link')}
                        rules={[{ required: true, message: t('validate.linkBlank') }]}
                      >
                        <Input
                          disabled={isBlockCtaLink}
                          placeholder={t('form.link')}
                          maxLength={255}
                          onChange={onChangeInput}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item name="logo" initialValue={imageList.length} label={t('form.logo')}>
                        <Upload {...action} maxCount={1} accept="image/*">
                          <Button
                            style={{ background: '#000', color: '#fff', borderRadius: '5px' }}
                            icon={<UploadOutlined />}
                          >
                            {t('form.upload')}
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row style={{ justifyContent: 'flex-end' }}>
                    <BtnContainer>
                      <ButtonActionPage
                        loading={isCreateLoading}
                        text={record ? t('form.btnUpdateHomeProduct') : t('form.btnCreateHomeProduct')}
                        id="1"
                        className="btn-submit-form-home"
                        htmlType="submit"
                        icon={!record ? <FileAddOutlined /> : undefined}
                      />
                    </BtnContainer>
                  </Row>
                </div>
              </StyledFormHomeProduct>
            </Row>
          </Form>
        </SwitchContainer>
      </SpinLoading>
    </>
  );
};

const SwitchContainer = styled.div`
  & .ant-form-item-label {
    font-weight: bold;
    font-size: 15px;
  }

  & .form-HomeProduct-container {
    padding: 1.5rem;

    .form-HomeProduct {
      resize: none;
    }
  }

  & .preview-home-product {
    border-radius: 18px;
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  }
`;

const BtnContainer = styled.div`
  & .btn-submit-form-home {
    width: 160px;
    bottom: -15px !important;
  }
`;

const StyledSelectImageCol = styled(Col)`
  padding: 0px !important;
  min-height: 400px;
  height: 520px;
  background-color: #efefef;
  position: relative;
  ${defaultImage}
`;

const StyledFormHomeProduct = styled(Col)`
  ${formScrolling}
  ${selectSelector}

  & input[type='text'],
  & input[type='number'] {
    ${appStyles.inputStyle}
  }

  & .add-partner {
    float: right;
    color: #3490ff;
    font-weight: bold;
    font-size: 14px;
    position: absolute;
    right: 0;
    z-index: 9999;
    background-color: transparent;
    border: none;
    cursor: pointer;
    text-decoration: underline;
    display: inline;
  }

  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & button.btn-submit-form-home {
    ${appStyles.commonButtonSubmitted}
  }

  & .ant-form-item {
    margin-bottom: 0.5rem;
  }

  & .ant-picker {
    width: 100%;
    padding: 0;
    ${appStyles.commonBorderRadius}
    & input {
      padding: 4px 0 4px 12px;
    }
    & .ant-picker-suffix {
      font-size: 20px;
      ${appStyles.background.black}
      ${appStyles.color.white}
      padding: 8px;
    }
  }

  & textarea {
    ${appStyles.commonBorderRadius}
  }

  & .ant-input-affix-wrapper {
    padding: 0 11px;
    ${appStyles.commonBorderRadius}
  }

  & .ant-form-item {
    ${appStyles.commonItemMarginBottom}
  }

  & .ant-form-item-explain {
    margin: 0;
  }

  & .ant-picker-clear {
    right: 40px;
  }

  & .ant-descriptions {
    cursor: default;
    & .ant-descriptions-header {
      margin-bottom: 5px;
    }
  }

  & .ant-upload-span {
    display: none !important;
  }

  & .popover {
    position: absolute;
    z-index: 2;
    bottom: 0;
    left: 10%;
  }

  & .popoverSub {
    position: absolute;
    z-index: 2;
    bottom: 0;
    right: 0;
  }

  & .cover {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  & .sketch-picker {
    cursor: pointer;
  }
`;
